import { array } from '@ember/helper';
import Button from 'tio-ui/components/button';
import { or } from 'tio-ui/utilities';
import { fn } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import { on } from '@ember/modifier';
import SearchInput from 'tio-ui/components/search-input';
import Select from 'tio-ui/components/select';
import { Section, VStack } from 'tio-ui/components/layout';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import Component from '@glimmer/component';
import pageTitle from 'ember-page-title/helpers/page-title';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
import Table from 'tio-ui/components/table';
import Paginator from 'tio-ui/components/paginator';
import TioPageBreadcrumbs from 'tio-common/components/tio/page/breadcrumbs';
import type EmployeeModel from 'tio-data/models/employee';
import type EmployeesIndexController from 'tio-employee/controllers/authenticated/admin/employees/index';
import type RouterService from '@ember/routing/router';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type PartnerService from 'tio-employee/services/partner';
import type { PaginatedCollection } from 'tio-common/types/ember-data';

export interface S {
  Args: {
    controller: EmployeesIndexController;
    model: PaginatedCollection<EmployeeModel>;
  };
}

export default class EmployeeIndexRoute extends Component<S> {
  @service declare partner: PartnerService;
  @service declare store: StoreService;
  @service declare router: RouterService;

  get plans() {
    return this.partner.company?.plans || [];
  }

  didSearch = (string: string) => {
    this.args.controller.search = string;
  };

  createEmployeeTransition = () => {
    this.router.transitionTo('authenticated.admin.employees.add');
  };

  onSort = (sort: string) => {
    this.args.controller.sort = sort;
  };

  previousPage = () => {
    this.args.controller.page = Math.max(1, this.args.controller.page - 1);
  };

  nextPage = () => {
    ++this.args.controller.page;
  };

  changePaginatorPage = (page: number) => {
    this.args.controller.page = page;
  };

  get totalPages() {
    const { model, controller } = this.args;
    const meta = model?.meta;

    if (meta?.page?.total) {
      return Math.ceil(meta.page.total / controller.limit);
    }
    return 0;
  }

  <template>
    {{pageTitle (t "partner.employees")}}

    <VStack>
      <div class="tio-flex-between">
        <TioPageBreadcrumbs as |b|>
          <b.crumb @label="Employees" />
          <b.crumb @label="Admin" />
        </TioPageBreadcrumbs>
        <Button
          @intent="primary"
          @appearance="outlined"
          {{on "click" this.createEmployeeTransition}}
        >
          {{t "partner.employee.create_employee"}}
        </Button>
      </div>
      <Section class="sticky top-0">
        <:body>
          <div class="w-1/4 inline-block align-bottom">
            <SearchInput
              @label={{t "partner.employee.search"}}
              @placeholder="Search (Name, Payroll ID, Email)"
              @value={{@controller.search}}
              @onChange={{this.didSearch}}
              @onSearch={{this.didSearch}}
              class="max-w-screen-sm"
            />
          </div>
          <div class="w-1/4 ml-2 inline-block align-bottom">
            <Select
              @label="Filter by plan"
              @items={{this.plans}}
              @selected={{@controller.plan}}
              @onChange={{fn (mut @controller.plan)}}
            />
          </div>
          <div class="w-1/4 ml-2 inline-block align-bottom">
            <Select
              @label="Filter by status"
              @clearable={{true}}
              @items={{array "Participating" "Pending" "Deactivated"}}
              @selected={{@controller.participationStatus}}
              @onChange={{fn (mut @controller.participationStatus)}}
            />
          </div>
        </:body>
      </Section>

      <Section class="overflow-x-auto">
        <:body>
          <Table @isLoading={{@model.isLoading}}>
            <:thead as |options|>
              <options.tr>
                <options.th
                  @sortField="payroll-id"
                  @activeSortField={{@controller.sort}}
                  @onSort={{this.onSort}}
                >
                  {{t "partner.employee.payroll_id"}}
                </options.th>
                <options.th
                  @sortField="first-name"
                  @activeSortField={{@controller.sort}}
                  @onSort={{this.onSort}}
                >
                  {{t "partner.employee.first_name"}}
                </options.th>
                <options.th
                  @sortField="last-name"
                  @activeSortField={{@controller.sort}}
                  @onSort={{this.onSort}}
                >
                  {{t "partner.employee.last_name"}}
                </options.th>
                <options.th
                  @sortField="email"
                  @activeSortField={{@controller.sort}}
                  @onSort={{this.onSort}}
                >
                  {{t "partner.employee.email"}}
                </options.th>
                <options.th
                  @sortField="participation-status"
                  @activeSortField={{@controller.sort}}
                  @onSort={{this.onSort}}
                >
                  {{t "partner.employee.status"}}
                </options.th>
                <options.th
                  @sortField="plan.company-key"
                  @activeSortField={{@controller.sort}}
                  @onSort={{this.onSort}}
                >
                  {{t "partner.employee.plan"}}
                </options.th>
                <options.th
                  @sortField="eligibility-start-date"
                  @activeSortField={{@controller.sort}}
                  @onSort={{this.onSort}}
                >
                  {{t "partner.employee.eligibility_start_date"}}
                </options.th>
                {{! using incorrect date }}
                <options.th
                  @sortField="created-at"
                  @activeSortField={{@controller.sort}}
                  @onSort={{this.onSort}}
                >
                  {{t "partner.employee.activation_date"}}
                </options.th>
                <options.th>{{t "partner.employee.accounts"}}</options.th>
                <options.th>{{t "partner.employee.loans"}}</options.th>
              </options.tr>
            </:thead>
            <:tbody as |options|>
              {{#each @model as |employee|}}
                <options.tr>
                  <options.td>
                    <LinkTo
                      class="text-primary-500 underline"
                      @route="authenticated.admin.employees.show"
                      @model={{employee.id}}
                    >
                      {{employee.payrollId}}
                    </LinkTo>
                  </options.td>
                  <options.td>
                    <LinkTo @route="authenticated.admin.employees.show" @model={{employee.id}}>
                      {{employee.firstName}}
                    </LinkTo>
                  </options.td>
                  <options.td>
                    <LinkTo @route="authenticated.admin.employees.show" @model={{employee.id}}>
                      {{employee.lastName}}
                    </LinkTo>
                  </options.td>
                  <options.td>{{employee.email}}</options.td>
                  <options.td>{{employee.participationStatus}}</options.td>
                  <options.td>{{employee.plan.name}}</options.td>
                  <options.td>
                    {{safeFormatDate
                      employee.eligibilityStartDate
                      month="long"
                      day="2-digit"
                      year="numeric"
                    }}
                  </options.td>
                  {{! this is the wrong date }}
                  <options.td>
                    {{safeFormatDate employee.createdAt month="long" day="2-digit" year="numeric"}}
                  </options.td>
                  <options.td>
                    <LinkTo @route="authenticated.admin.employees.show" @model={{employee.id}}>
                      {{employee.statistics.user.total_accounts}}
                    </LinkTo>
                  </options.td>
                  <options.td>
                    <LinkTo @route="authenticated.admin.employees.show" @model={{employee.id}}>
                      {{employee.statistics.user.total_loans}}
                    </LinkTo>
                  </options.td>
                </options.tr>
              {{/each}}
            </:tbody>
            <:tfoot>
              <tr>
                <td class="text-right" colspan="9">
                  <Paginator
                    @simple={{true}}
                    @currentPage={{@controller.page}}
                    @totalItems={{or @model.meta.page.total 0}}
                    @pageSize={{@controller.limit}}
                    @onChange={{this.changePaginatorPage}}
                  />
                </td>
              </tr>
            </:tfoot>
          </Table>
        </:body>
      </Section>
    </VStack>
  </template>
}
